import { defu } from 'defu'
import { omit } from 'es-toolkit'
import { promiseTimeout } from '@vueuse/core'
import { type UseQueryOverrideOptions, type BrandUnion, type ConsentComponents, type PaymentServiceComponents, ConsentRequestVerification_status, type UIAlertProps, BroadcastMessageType, FailedDepositReason } from '@core/types'
import { type RedirectUrls, PspEnum, DepositViewEnum, type UserAccount, MethodEnum, type CreditCardDataObject, type MethodUnion, type PaymentMethod, type PspUnion, AmountValidationEnum, type PaymentMethodFee, type CashierDepositDataObject, type QuickDepositDataObject, type QuickDepositRequestData, type CashierDepositRequestData, type FirstDepositTypeUnion, FirstDepositTypeEnum, FirstDepositTypeResponseEnum, type CashierDepositDataObjectAttributes, type UnifiedDepositResponse, type CashierDepositResponseData } from '@payments/types'
import { getMajorUnitDepositAmount, getMinorUnitDepositAmount } from '@payments/utils/depositAmount'
import { computed, ref, useState, useApi, until, useRouter, useNuxtData, type MaybeRefOrGetter, usePlaygameDeposit, useDepositClient, useGtag, useZaraz, useSignupVersion, useMutation, useMoneyLimits, useCoreConfig, useCookieConsent, toValue, nextTick, navigateTo, useConsentClient, usePayments, useDevice, useRoute, useFormat, useDepositPromotion, usePaymentsConfig, useQuery, firstToUpper, usePaymentsClient, useI18n, useTimeoutFn, useTracking, useApplePayScript, useGooglePayScript, useMobilepayScript, useScript, OpenapiError, useQueryClient, mediaUrl, versionedEventName, USER_INFO_KEY, useSharedBroadcast, useApm, onApmReady, shallowRef, toError, markDepositStarted } from '#imports'

const PENDING_PAGE_PATH = /^\/payments\/pending\/([^/]+)$/
// What a receipt needs in the URL; the rest of the page's query (e.g. its filters) stays when the receipt takes them off
const RECEIPT_QUERY_KEYS = ['txId', 'brand', 'psp']

type FailedDepositRequest = PaymentServiceComponents['schemas']['FailedDepositRequest']

// What a failure report is filed under, taken when the attempt starts: the failure can arrive after the player changed the selection
interface DepositAttempt extends Pick<FailedDepositRequest, 'psp' | 'paymentMethod' | 'accountId' | 'amount' | 'brand'> {
    reported: boolean
}

export const useDeposit = () => {
    const api = useApi()
    // The RUM agent installs on nuxt-ready; useDeposit may run before that
    const apm = shallowRef(useApm())
    onApmReady((agent) => {
        apm.value = agent
    })
    const paymentsApi = useApi({ baseURL: '/gateway/payments', service: 'payments' })
    const route = useRoute()
    const router = useRouter()
    const { invalidateQueries } = useQueryClient()
    const signupVersion = useSignupVersion()
    const zaraz = useZaraz()
    const { trackEvent } = useTracking()
    const { amountValue, amount, message, balance, selectedAccount, isPspPaymentIQ, isPspEpay, isTransactionSuccessful, isTransactionFailed, isTransactionInconsistent, isPspDummy, isPspTrueLayer, isApplePay, isGooglePay, isPayPal, isMobilepay, isCreditCard, processing, resetMessage, setSelectedAmount, formatCardNumber, accountDetailsQuery } = usePayments()
    const { asMoney } = useFormat()
    const { client } = usePaymentsClient()
    const { t } = useI18n()
    const { deposit, paymentIq, pendingPageRedirectUrl } = usePaymentsConfig()
    const { gtag } = useGtag()
    const { hasDepositPromotion, promotionBonus, isPromotionToggleable, promotionBonusMaxAmount, isPromotionToggled, activatePromotionMutation, hasPromotion, isActivePromotion, isAmountWithinBonusRequirement, promotion } = useDepositPromotion()
    const { depositRemainingLimit, highRiskDepositLimitReached, hasReachedDepositLimit: hasReachedMoneyDepositLimit, isDepositLimitEnabled, requireMoneyLimits, limitTypesWithLimits, refreshLimitData, showDialog: showMoneyLimitsDialog } = useMoneyLimits()
    const { site, locale } = useCoreConfig()
    const { sendMessage } = useSharedBroadcast()
    const { consent: cookieConsent } = useCookieConsent()
    const { updateConsent, getConsent } = useConsentClient()
    const { userAgent, isMobile } = useDevice()
    const { game } = usePlaygameDeposit()
    const { useConfig, usePaymentMethods, useUnprocessedWithdrawals, reportFailedDeposit } = useDepositClient()

    // How long a deposit waits for the ePay SDK to report itself ready before giving up
    // and showing an error instead of hanging on a promise that never settles.
    const EPAY_LOAD_TIMEOUT = 15_000
    // Initial poll plus 20 retries, as the pending page always did
    const TRANSACTION_STATUS_MAX_ATTEMPTS = 21
    // Short: the player waits on the deposit with no page behind it, and the pending page fallback polls again in full
    const EPAY_IN_PAGE_STATUS_MAX_ATTEMPTS = 8
    const TRANSACTION_STATUS_POLL_INTERVAL = 1_000

    // Bump this constant when the customer funds consent wording changes so that
    // existing users (who accepted the previous version) are shown the acknowledgement
    const CUSTOMER_FUNDS_CONSENT_VERSION = 2

    const FIRST_DEPOSIT_CONSENT_TEXT = `
        I accept that ${site.title} keeps customer funds in a dedicated account, separate from company funds,
        with steps taken to protect them, though full protection cannot be guaranteed in the unlikely event of
        insolvency: "Medium protection", see our
        <a href="/terms#p4-9_fund-disclosure" target="_blank" rel="noopener">T&C's section 4.9</a>.
    `

    const CHANGE_ACKNOWLEDGEMENT_CONSENT_TEXT = `
        <span style="display: block; margin-bottom: 1.5em;">Good news, your customer funds protection has changed for the better.</span>
        I accept that ${site.title} now keeps customer funds in a dedicated account, separate from company funds,
        with steps taken to protect them, though full protection cannot be guaranteed in the unlikely event of
        insolvency: "Medium protection", see our
        <a href="/terms#p4-9_fund-disclosure" target="_blank" rel="noopener">T&C's section 4.9</a>.
    `

    const FIRST_DEPOSIT_TYPE_EVENT = {
        [FirstDepositTypeResponseEnum.ULTIMATE]: FirstDepositTypeEnum.ULTIMATE,
        [FirstDepositTypeResponseEnum.SOFT]: FirstDepositTypeEnum.SOFT,
        [FirstDepositTypeResponseEnum.RETURNED]: FirstDepositTypeEnum.RETURNED,
    }

    const { data: config } = useNuxtData('deposit:config')
    const { data: paymentMethodsData } = useNuxtData('deposit:payment-methods')
    const { data: unprocessedWithdrawals } = useNuxtData('deposit:unprocessed-withdrawals')
    const { data: userData } = useNuxtData(USER_INFO_KEY)
    const { data: depositSuccessfulCount } = useNuxtData('payments:cashier:deposit:successful-count')
    const { data: userAccounts } = useNuxtData<UserAccount[]>('deposit:user-accounts')

    const { isSettled: isApplePaySettled, load: loadApplePay } = useApplePayScript()
    const { isLoaded: isGooglePayLoaded, load: loadGooglePay } = useGooglePayScript()
    const { isLoaded: isMobilepayLoaded, load: loadMobilepay } = useMobilepayScript()

    function loadWalletScripts() {
        loadApplePay()
        loadGooglePay()
        if (deposit.useMobilepay) loadMobilepay()
    }

    const canUseApplePay = computed(() => {
        if (!isApplePaySettled.value) return false
        if (!import.meta.client) return false

        return !!window.ApplePaySession?.canMakePayments()
    })

    // Availability is a property of the client, not of the server's response, so it is
    // derived on read: the list re-filters when a wallet SDK settles.
    const paymentMethods = computed<PaymentMethod[]>(() => {
        if (!Array.isArray(paymentMethodsData.value)) return []

        return paymentMethodsData.value.filter((method: PaymentMethod) => {
            const paymentMethod = method.paymentMethod

            if (!paymentMethod) return true
            if (isApplePay(paymentMethod)) return canUseApplePay.value
            if (isGooglePay(paymentMethod)) return isGooglePayLoaded.value
            if (isMobilepay(paymentMethod) && isPspEpay(method.psp)) return isMobilepayLoaded.value

            return true
        })
    })
    const isEpayLoaded = useState<boolean>('deposit:epay-loaded', () => false)
    const isEpayTransactionAccepted = useState<boolean>('deposit:epay-transaction-accepted', () => false)
    const consent = useState<boolean>('deposit:consent', () => !deposit.useFirstDepositConsent)
    const creditCardFields = useState<CreditCardDataObject | undefined>('deposit:credit-card-fields')
    const fee = useState<number | null>('deposit:fee', () => null)
    const hasRejectedSignupPromotion = useState<boolean>('deposit:has-rejected-signup-promotion', () => false)
    const hasShownAmountBumpModal = useState<boolean>('deposit:has-shown-amount-bump-modal', () => false)
    const isManualCorrectionActive = useState<boolean>('deposit:is-manual-correction-active', () => false)
    const { start: resetManualCorrection } = useTimeoutFn(() => {
        isManualCorrectionActive.value = false
    }, 100, { immediate: false })
    const isQuickPay = useState<boolean>('deposit:is-quick-pay', () => false)
    const needsConfirmation = useState<boolean>('deposit:needs-confirmation', () => false)
    const requestDataObject = useState<CashierDepositDataObject | QuickDepositDataObject | 'epay' | undefined>('deposit:request-data-object')
    const selectedDepositMethod = useState<MethodUnion | undefined>('deposit:selected-deposit-method')
    const showAmountBumpModal = useState<boolean>('deposit:show-amount-bump-modal', () => false)
    const storeAccount = useState<boolean>('deposit:store-account', () => false)
    const signalResetDeposit = useState<boolean>('deposit:signal-reset-deposit', () => false)
    const depositAttempt = useState<DepositAttempt | undefined>('deposit:attempt')
    // The ePay SDK keeps the callbacks of its first load while later attempts swap the session under it,
    // so its failures are filed under the attempt that set up or last drove the current session
    const epayAttempt = useState<DepositAttempt | undefined>('deposit:epay-attempt')

    const { data: consents, refresh: refreshConsent } = getConsent({ lazy: true, enabled: false })

    const { start: startConfirmationDelay } = useTimeoutFn(() => {
        processing.value = false
        needsConfirmation.value = true
    }, 500, { immediate: false })

    const bumpAmount = computed(() => {
        return deposit.bumpAmount || 10
    })

    const isRemainingDepositLimitEligibleForAmountBump = computed(() => {
        if (!isDepositLimitEnabled.value) return true
        if (Number.isNaN(remainingLimit.value)) return true

        return amountValue.value + bumpAmount.value <= remainingLimit.value
    })

    const isAmountBelowBonusMinimum = computed(() => !!promotionBonus.value && !isAmountWithinBonusRequirement(amountValue.value))

    const isAmountAboveBonusMaximum = computed(() => bumpAmount.value + amountValue.value > promotionBonusMaxAmount.value)

    async function tryShowAmountBumpModal(withConfirmation: boolean): Promise<boolean> {
        if (!showAmountBumpModalBeforeDeposit.value) {
            if (hasShownAmountBumpModal.value) {
                trackEvent({
                    event_name: 'amount_bump_dialog_blocked_by_has_shown_bump_already',
                    event_type: 'Load',
                    meta_data: [{ category: 'Deposit' }]
                })
            }
            else if (isActivePromotion.value) {
                if (isAmountBelowBonusMinimum.value) {
                    trackEvent({
                        event_name: 'amount_bump_dialog_blocked_by_amount_below_bonus_minimum',
                        event_type: 'Load',
                        meta_data: [{ category: 'Deposit' }]
                    })
                }
                else if (isAmountAboveBonusMaximum.value) {
                    trackEvent({
                        event_name: 'amount_bump_dialog_blocked_by_amount_above_bonus_maximum',
                        event_type: 'Load',
                        meta_data: [{ category: 'Deposit' }]
                    })
                }
            }

            return false
        }

        if (!isRemainingDepositLimitEligibleForAmountBump.value) {
            trackEvent({
                event_name: 'amount_bump_dialog_blocked_by_deposit_limit',
                event_type: 'Load',
                meta_data: [{ category: 'Deposit' }]
            })

            return false
        }

        showAmountBumpModal.value = true
        processing.value = false

        if (withConfirmation)
            needsConfirmation.value = true

        return true
    }

    const hasAccount = computed(() => userAccounts.value && userAccounts.value?.length > 0)

    // Any set limit counts as having answered the AC Finten prompt — only prompt users
    // with no limits at all, even while another enabled type is unanswered.
    const requiresLimitAnswer = computed(() => requireMoneyLimits.value && !limitTypesWithLimits.value.length)

    const useAcFinten = computed(() => deposit.useAcFinten && requiresLimitAnswer.value)

    // Hoisted so ensureFinancialLimitsWithAcFinten can await it with a single until() —
    // racing two until() calls leaked the losing watcher on every deposit attempt.
    const limitAnswerSettled = computed(() => !requiresLimitAnswer.value || !showMoneyLimitsDialog.value)

    const isSeamlessWallet = computed(() => accountDetailsQuery.data.value?.game_wallet_type === 'SEAMLESS')

    const isFirstDeposit = computed(() => depositSuccessfulCount.value?.count === 0)

    const hasSavedFirstDepositConsent = computed(() => {
        if (!deposit.useFirstDepositConsent) return true

        // While consents are still loading, use the same safe default as before:
        // existing depositors are treated as having consent, new users are not
        if (consents.value === null || consents.value === undefined) return !isFirstDeposit.value

        // Consent type 5 is the customer funds consent; version must be current
        return !!consents.value.find(consent =>
            consent.type === 5
            && consent.verification_status === ConsentRequestVerification_status.ACCEPTED
            && Number(consent.version) >= CUSTOMER_FUNDS_CONSENT_VERSION
        )
    })

    const depositConsentText = computed(() =>
        !isFirstDeposit.value && !hasSavedFirstDepositConsent.value
            ? CHANGE_ACKNOWLEDGEMENT_CONSENT_TEXT
            : FIRST_DEPOSIT_CONSENT_TEXT
    )

    const hasFirstDepositConsent = computed(() => consent.value || hasSavedFirstDepositConsent.value)

    const fees = computed(() => config.value?.fees)

    const isFeeEnabled = computed(() => config.value?.fees?.length)

    const minAmount = computed(() => Number(config.value?.limits?.min) || 0)

    const maxAmount = computed(() => Number(config.value?.limits?.max) || 0)

    const amountTooLow = computed(() => minAmount.value && amountValue.value < minAmount.value)

    const isSecondaryView = computed(() => [DepositViewEnum.CARD_CONSENT].includes(route.query.view as DepositViewEnum))

    const remainingLimit = computed(() => Number(depositRemainingLimit.value?.remaining))

    const options = computed(() => config.value?.options.sort((a: string, b: string) => Number(a) - Number(b)))

    const hasReachedDepositLimit = computed(() => {
        if (!isDepositLimitEnabled.value) return false
        if (highRiskDepositLimitReached.value) return true
        if (!minAmount.value) return true

        const hasDepositableOption = options.value?.length
            ? options.value.some((option: number | string) => Number(option) >= minAmount.value)
            : false

        if (!hasDepositableOption) return false

        return hasReachedMoneyDepositLimit(minAmount.value)
    })

    function canMakeWalletDeposit(method: (m?: MethodUnion) => boolean | undefined) {
        if (showAmountBumpModal.value) return false
        if (!needsConfirmation.value) return false
        if (isInvalidAmount.value) return false
        if (!selectedPaymentMethod.value) return false

        return !!method(selectedPaymentMethod.value)
    }

    const canMakeApplePayDeposit = computed(() => canMakeWalletDeposit(isApplePay))
    const canMakeGooglePayDeposit = computed(() => canMakeWalletDeposit(isGooglePay))
    const canMakeMobilepayDeposit = computed(() => isPspEpay(selectedPaymentMethodObject.value?.psp) && canMakeWalletDeposit(isMobilepay))

    const canMakeExternalPaymentDeposit = computed(() => {
        if (!selectedPaymentMethodObject.value?.psp) return false

        return canMakeApplePayDeposit.value || canMakeGooglePayDeposit.value || canMakeMobilepayDeposit.value
    })

    const selectedPaymentMethodObject = computed<PaymentMethod | UserAccount | undefined>(() => {
        if (isQuickPay.value) {
            if (!selectedAccount.value) return
            if (!userAccounts.value?.length) return

            return userAccounts.value.find(account => account.id === selectedAccount.value) || undefined
        }

        if (!selectedDepositMethod.value) return

        return paymentMethods.value.find((method: PaymentMethod) => method.paymentMethod === selectedDepositMethod.value) || undefined
    })

    const selectedPaymentMethod = computed<MethodEnum | undefined>(() => (selectedPaymentMethodObject.value as UserAccount)?.type ?? (selectedPaymentMethodObject.value as PaymentMethod)?.paymentMethod)

    function getDepositRequestAmount(amount: number): string {
        const requestAmount = isPspEpay(selectedPaymentMethodObject.value?.psp)
            ? getMinorUnitDepositAmount(amount)
            : getMajorUnitDepositAmount(amount)

        return requestAmount.toString()
    }

    const isExternalPaymentButton = computed(() => {
        if (isApplePay(selectedPaymentMethod.value)) return true
        if (isGooglePay(selectedPaymentMethod.value)) return true
        if (isMobilepay(selectedPaymentMethod.value) && isPspEpay(selectedPaymentMethodObject.value?.psp)) return true

        return false
    })

    const showTransactionOverview = computed(() => {
        if (!isFeeEnabled.value) return false
        if (!selectedPaymentMethod.value) return false

        if (isQuickPay.value) {
            if (isFeeEnabledForPaymentMethod(selectedPaymentMethod.value)) return true

            if (hasPromotion.value) {
                if (!isDepositPromotionDisabled()) {
                    if (isPromotionToggleable.value) return isPromotionToggled.value

                    return true
                }
            }
        }

        return isFeeEnabledForPaymentMethod(selectedPaymentMethod.value)
    })

    const skipNeedsConfirmation = computed(() => {
        if (isQuickPay.value) return false
        if (!selectedPaymentMethod.value) return false
        if (isApplePay(selectedPaymentMethod.value)) return false

        return true
    })

    const showAmountBumpModalBeforeDeposit = computed(() => {
        if (hasShownAmountBumpModal.value) return false
        if (showAmountBumpModal.value) return false
        if (!deposit.useAmountBumpModal) return false
        if (!isActivePromotion.value) return false
        if (isAmountBelowBonusMinimum.value) return false

        return !isAmountAboveBonusMaximum.value
    })

    const creditCardPsp = computed(() => {
        const creditCardMethod = paymentMethods.value.find((method: PaymentMethod) => isCreditCard(method.paymentMethod))

        if (!creditCardMethod) return null

        return creditCardMethod.psp
    })

    const isGameRestartRequired = computed(() => !!game.value)

    function handleEpayTransactionAccepted() {
        markDepositStarted()
        isEpayTransactionAccepted.value = true
    }

    // After transactionAccepted, ePay redirects to our successUrl (/payments/pending/<txId>). Confirming in-page
    // instead keeps a running game alive and skips the full reload. Redirects before acceptance (3DS) pass through.
    // True when the deposit is confirmed in-page: the caller then cancels ePay's redirect.
    function handleEpayAcceptedRedirect(url: string): boolean {
        markDepositStarted()

        if (!isEpayTransactionAccepted.value) return false

        const txId = new URL(url, location.origin).pathname.match(PENDING_PAGE_PATH)?.[1]

        if (!txId || txId === 'null') return false

        isEpayTransactionAccepted.value = false
        // The external payment buttons (Apple Pay, Google Pay) clear it before paying; the player waits on the status now
        processing.value = true

        // ePay only cancels its redirect on a synchronous `false`, so the in-page confirmation runs unawaited
        confirmEpayDepositInPage(txId, url)
            .catch((error: unknown) => {
                apm.value.captureError(toError(error))

                return handleDepositRedirect(url)
            })
            .catch((error: unknown) => apm.value.captureError(toError(error)))

        return true
    }

    async function confirmEpayDepositInPage(txId: string, pendingUrl: string) {
        // The page-scoped useRoute() is frozen once the page changes, so compare against the router's live route
        const startPath = router.currentRoute.value.path

        // The receipt and its conversion tracking need the backend-settled transaction. Anything else goes through
        // the pending page as before, which keeps polling and shows the outcome.
        const isSettledSuccessfully = await waitForTransactionStatus(txId, PspEnum.EPAY, { maxAttempts: EPAY_IN_PAGE_STATUS_MAX_ATTEMPTS }).then(
            status => !!isTransactionSuccessful(status?.state),
            () => false,
        )

        // Wherever the player is now, a failed or still pending deposit must show its outcome
        if (!isSettledSuccessfully) {
            await handleDepositRedirect(pendingUrl)

            return
        }

        // Without the pending page's reload nothing else ends the deposit: playgame's togglePayments skips while
        // requestDataObject is set, and a quick deposit stays on its view, so App.vue's view watcher never resets it
        resetPayment()

        const currentRoute = router.currentRoute.value
        const receiptQuery = { txId, brand: site.brand, psp: PspEnum.EPAY }

        if (currentRoute.meta.isPlaygame) {
            sendMessage({
                type: BroadcastMessageType.PLAYGAME_DEPOSIT_SUCCESSFUL,
                data: receiptQuery,
            })

            return
        }

        // The player moved on while the transaction settled: show the receipt where they are instead of pulling them back
        if (currentRoute.path !== startPath) {
            await navigateTo({ query: { ...currentRoute.query, ...receiptQuery } })

            return
        }

        await navigateTo({
            path: pendingPageRedirectUrl?.deposit ?? t('pages.lobby'),
            query: receiptQuery,
        })
    }

    async function insertEpaySDK() {
        if (!isPspEpay(selectedPaymentMethodObject.value?.psp)) return

        const attempt = startDepositAttempt()

        await epayFirstDepositOptionsMutation.mutateAsync({}, {
            onSuccess: (data) => {
                if (!data?.pspClient) return
                // capture so the narrowing survives into the onLoaded closure below
                const pspClient = data.pspClient

                epayAttempt.value = attempt

                if (isEpayLoaded.value) {
                    // Already loaded, use a new session
                    window.epay
                        .setSessionId(pspClient.attributes.id as string)
                        .setSessionKey(pspClient.key)

                    return
                }

                const epayScript = useScript({
                    src: pspClient.clientUrl,
                }, {
                    trigger: 'client',
                })

                epayScript.onLoaded(async () => {
                    await window.epay
                        .setSessionId(pspClient.attributes.id as string)
                        .setSessionKey(pspClient.key)
                        .setCallbacks({
                            clientReady: () => {
                                document.dispatchEvent(new CustomEvent('epayClientReady'))

                                isEpayLoaded.value = true
                            },
                            transactionAccepted: handleEpayTransactionAccepted,
                            clientRedirect: (event) => {
                                if (handleEpayAcceptedRedirect(event.context.url as string)) return false

                                // Ensure that a new window is opened in playgame
                                if (route.meta.isPlaygame || isExternalPaymentButton.value) {
                                    handleDepositRedirect(event.context.url as string)

                                    return false
                                }
                            },
                            inputValidity: (event) => {
                                document.dispatchEvent(new CustomEvent('epayInputValidity', { detail: event }))
                            },
                            inputSubmit: () => {
                                document.dispatchEvent(new CustomEvent('epayInputSubmit'))

                                return false
                            },
                            invalidInput: () => {
                                resetPayment(t('core.defaultErrorMessage'))
                            },
                            error: (error) => {
                                if (isFirstDeposit.value) {
                                    trackEvent({
                                        event_name: 'signup_deposit_endpoint_error_epay_error',
                                        event_type: 'Load',
                                        meta_data: [
                                            {
                                                category: 'Deposit',
                                                errorMessage: error?.message,
                                                errorCode: error?.errorCode,
                                            },
                                        ]
                                    })
                                }
                                else {
                                    trackEvent({
                                        event_name: 'deposit_epay_error',
                                        event_type: 'Load',
                                        meta_data: [
                                            {
                                                category: 'Deposit',
                                                errorMessage: error?.message,
                                                errorCode: error?.errorCode,
                                            },
                                        ]
                                    })
                                }

                                reportDepositFailure(epayAttempt.value, FailedDepositReason.psp_error, error)

                                // Session expired and the user must type card information again
                                if (['UNEXPECTED_SESSION_STATE', 'INVALID_OR_EXPIRED_SESSION'].includes(error?.errorCode)) {
                                    router.go(-1)
                                }

                                resetPayment(t('core.defaultErrorMessage'))
                            },
                            paymentCancel: () => reportDepositFailure(epayAttempt.value, FailedDepositReason.cancelled),
                        })
                        .init()
                })

                epayScript.onError(() => {
                    isEpayLoaded.value = false

                    if (isFirstDeposit.value) {
                        trackEvent({
                            event_name: 'signup_deposit_load_error_epay_script',
                            event_type: 'Load',
                            meta_data: [
                                { category: 'Deposit' }
                            ]
                        })
                    }
                    else {
                        trackEvent({
                            event_name: 'deposit_epay_script_load_error',
                            event_type: 'Load',
                            meta_data: [
                                { category: 'Deposit' }
                            ]
                        })
                    }

                    reportDepositFailure(attempt, FailedDepositReason.setup_failed)
                })
            },
            onError: (error) => {
                if (isFirstDeposit.value) {
                    trackEvent({
                        event_name: 'signup_deposit_endpoint_error_epay_deposit_options',
                        event_type: 'Load',
                        meta_data: [
                            {
                                category: 'Deposit',
                                errorObject: error,
                            },
                        ]
                    })
                }

                reportDepositFailure(attempt, FailedDepositReason.setup_failed, error)

                router.go(-1)
            }
        })
    }

    // The ePay SDK is inserted lazily by whichever view needs it first (hosted fields, external
    // payment buttons), so a deposit can be started from a view that never loaded it, or with the
    // script blocked or still in flight. Every `window.epay` call has to go through here, otherwise
    // the click throws on an undefined global and the user is left with nothing.
    // `newSession` forces a fresh ePay session, which the external payment buttons need on every
    // attempt because their previous session is spent.
    async function ensureEpayLoaded(newSession = false) {
        if (newSession || !isEpayLoaded.value) {
            await insertEpaySDK()
        }

        if (!isEpayLoaded.value) {
            await until(isEpayLoaded).toBe(true, { timeout: EPAY_LOAD_TIMEOUT, throwOnTimeout: false })
        }

        if (window.epay) return true

        trackEvent({
            event_name: 'deposit_epay_sdk_unavailable',
            event_type: 'Load',
            meta_data: [{
                category: 'Deposit',
                psp: selectedPaymentMethodObject.value?.psp,
                paymentMethod: selectedPaymentMethod.value,
                view: route.query.view,
                isQuickPay: isQuickPay.value,
            }]
        })

        resetPayment(t('core.defaultErrorMessage'))

        return false
    }

    function isDepositPromotionDisabled(method?: MethodUnion) {
        if (!hasPromotion.value) return false

        if (method) {
            if (isPromotionToggleable.value) {
                return isPromotionToggled.value && isPayPal(method)
            }

            return isPayPal(method)
        }

        // Display promotion regardless when the user is selecting a method
        if (route.query.view === DepositViewEnum.CASHIER_METHODS) return false

        // Display promotion if the user hasn't selected a method yet
        if (!selectedPaymentMethod.value) return false

        // Hide the promotion in quick deposit, if the selected method is PayPal
        if (isQuickPay.value && route.query.view === DepositViewEnum.CREDIT_AND_PAYMENT) return isPayPal(selectedPaymentMethod.value)

        if (isPromotionToggleable.value) {
            return isPromotionToggled.value && isPayPal(selectedPaymentMethod.value)
        }

        return isPayPal(selectedPaymentMethod.value)
    }

    function getFeeObjectForPaymentMethod(paymentMethod: MethodEnum) {
        if (!isFeeEnabled.value) return {}
        if (!fees.value?.length) return {}

        const feeObject = fees.value.find((fee: PaymentMethodFee) => fee.paymentMethod === paymentMethod)

        if (!feeObject) return {}

        return feeObject
    }

    function isFeeEnabledForPaymentMethod(paymentMethod: MethodEnum) {
        const feeObjectForPaymentMethod = getFeeObjectForPaymentMethod(paymentMethod)

        if (!feeObjectForPaymentMethod) return false

        return !!(feeObjectForPaymentMethod as PaymentMethodFee)?.paymentMethod
    }

    const isFeeLoading = computed(() =>
        isFeeEnabledForPaymentMethod(selectedPaymentMethod.value as MethodEnum) && fee.value === null
    )

    function isFlatFeeOnly(paymentMethod: MethodEnum) {
        const feeObjectForPaymentMethod = getFeeObjectForPaymentMethod(paymentMethod)

        return (feeObjectForPaymentMethod as PaymentMethodFee)
            ? !Number((feeObjectForPaymentMethod as PaymentMethodFee)?.percentageFee)
            : false
    }

    function formattedPaymentMethodShort(account: UserAccount | PaymentMethod): string {
        const type = (account as UserAccount)?.type || (account as PaymentMethod)?.paymentMethod

        if (!type) return ''

        const hasPublicIdentifier = (account as UserAccount)?.publicIdentifier

        switch (type.toLowerCase()) {
            case MethodEnum.CREDITCARD.toLowerCase():
                if (hasPublicIdentifier) return formatCardNumber((account as UserAccount).publicIdentifier as string).slice(-5)

                return firstToUpper(type)

            case MethodEnum.BANK.toLowerCase():
            case MethodEnum.TRUELAYER.toLowerCase():
                return 'Instant Bank Payment'

            case MethodEnum.APPLEPAY.toLowerCase():
                return 'Apple Pay'

            case MethodEnum.MOBILEPAY.toLowerCase():
                return 'MobilePay'

            case MethodEnum.PAYPAL.toLowerCase():
                return 'PayPal'

            case MethodEnum.GOOGLEPAY.toLowerCase():
                return 'Google Pay'

            default:
                return firstToUpper(type)
        }
    }

    function getSubtitleForPaymentMethod(method: MethodEnum) {
        if (isDepositPromotionDisabled(method))
            return t('payments.deposit.bonusDisabledByMethod')

        if (!isFeeEnabledForPaymentMethod(method))
            return t('payments.common.noFee')

        if (isFlatFeeOnly(method)) {
            const feeObject = getFeeObjectForPaymentMethod(method) as PaymentMethodFee

            return `${asMoney(Number(feeObject.flatFee), { removeTrailingZeros: true })} ${t('core.fee')}`
        }

        return t('payments.common.feeApplies')
    }

    function getProviderDescription(method: string): string {
        if (!method) return ''

        const providerDescription = t(`payments.deposit.methods.${method.toLowerCase()}`)

        if (!providerDescription)
            return firstToUpper(method)

        return providerDescription
    }

    function getImageForPaymentMethod(method: MethodEnum) {
        const lowerCaseMethod = method.toLowerCase() as MethodEnum

        if ([MethodEnum.TRUELAYER.toLowerCase(), MethodEnum.BANK.toLowerCase()].includes(lowerCaseMethod))
            return mediaUrl('/media/cards/truelayer.svg')

        return mediaUrl(`/media/cards/${method.toLowerCase()}.svg`)
    }

    const isInvalidAmount = computed(() => {
        if (amountTooLow.value) {
            return AmountValidationEnum.TOO_LOW
        }

        if (isDepositLimitEnabled.value) {
            if (remainingLimit.value <= maxAmount.value && amountValue.value > remainingLimit.value) {
                return AmountValidationEnum.MORE_THAN_REMAINING_LIMIT
            }
        }
        if (Number(fee.value) >= amountValue.value) {
            return AmountValidationEnum.LOWER_THAN_FEE
        }

        if (amountValue.value > maxAmount.value) {
            return AmountValidationEnum.TOO_HIGH
        }

        return false
    })

    function resetPayment(errorMessage = '') {
        resetMessage()
        fee.value = null
        processing.value = false
        hasShownAmountBumpModal.value = false
        requestDataObject.value = undefined
        showAmountBumpModal.value = false
        needsConfirmation.value = false
        isEpayTransactionAccepted.value = false

        if (errorMessage) {
            message.value = {
                type: 'danger',
                message: errorMessage
            }
        }
    }

    function getSelectedDepositAttempt(): Omit<DepositAttempt, 'reported'> | undefined {
        const psp = selectedPaymentMethodObject.value?.psp

        if (!psp || !selectedPaymentMethod.value) return

        return {
            psp: psp as FailedDepositRequest['psp'],
            paymentMethod: selectedPaymentMethod.value as unknown as FailedDepositRequest['paymentMethod'],
            accountId: isQuickPay.value ? selectedAccount.value : undefined,
            // A cleared amount input holds ''
            amount: Number(amountValue.value) || 0,
            brand: site.brand as FailedDepositRequest['brand'],
        }
    }

    function startDepositAttempt(attempt = getSelectedDepositAttempt()) {
        depositAttempt.value = attempt && { ...attempt, reported: false }

        return depositAttempt.value
    }

    function setDepositAttemptAmount(amount: number) {
        if (depositAttempt.value) depositAttempt.value.amount = amount
    }

    // Called from failure handlers, so it must never throw into them
    function reportDepositFailure(attempt: DepositAttempt | undefined, reason: FailedDepositReason, error?: unknown) {
        if (!attempt || attempt.reported) return

        attempt.reported = true

        try {
            const { errorCode, message } = (error ?? {}) as { errorCode?: unknown, message?: unknown }
            // An OpenapiError's own message is the generic "[POST] Request to … failed"; the server's is in its body
            const errorMessage = (OpenapiError.isError(error) ? error.data?.message : undefined) || message

            reportFailedDeposit({
                ...omit(attempt, ['reported']),
                reason,
                errorCode: errorCode == null ? undefined : String(errorCode).slice(0, 255),
                errorMessage: errorMessage == null ? undefined : String(errorMessage).slice(0, 500),
            })
        }
        catch (reportError) {
            apm.value.captureError(toError(reportError))
        }
    }

    function resetAmount() {
        amount.value = undefined
        amountValue.value = 0
    }

    function resetDepositApp() {
        resetPayment()
        resetAmount()

        selectedDepositMethod.value = undefined
        isEpayLoaded.value = false
        creditCardFields.value = undefined
        hasRejectedSignupPromotion.value = false
        storeAccount.value = false
    }

    function validateConsent() {
        resetMessage()

        if (hasFirstDepositConsent.value) return false

        if (!consent.value) {
            return t('payments.deposit.errors.invalid.paymentConsent')
        }

        return false
    }

    async function validateAmount() {
        if (!isInvalidAmount.value) return

        switch (isInvalidAmount.value) {
            case AmountValidationEnum.TOO_LOW:
                await nextTick()

                return {
                    type: 'danger' as UIAlertProps['type'],
                    errorKey: AmountValidationEnum.TOO_LOW,
                    message: t('payments.deposit.minAmount', { amount: asMoney(minAmount.value, { decimals: 0 }) })
                }

            case AmountValidationEnum.TOO_HIGH:
                await nextTick()

                return {
                    type: 'warning' as UIAlertProps['type'],
                    errorKey: AmountValidationEnum.TOO_HIGH,
                    adjustedAmount: asMoney(maxAmount.value, { decimals: 0 }),
                    message: t('payments.deposit.maxAmount', { amount: asMoney(maxAmount.value, { decimals: 0 }) })
                }

            case AmountValidationEnum.MORE_THAN_REMAINING_LIMIT:
                await nextTick()

                return {
                    type: 'warning' as UIAlertProps['type'],
                    errorKey: AmountValidationEnum.MORE_THAN_REMAINING_LIMIT,
                    adjustedAmount: asMoney(Number(remainingLimit.value), { decimals: 0 }),
                    message: t('payments.deposit.maxAmountLimit', { amount: asMoney(remainingLimit.value, { decimals: 0 }) })
                }

            case AmountValidationEnum.LOWER_THAN_FEE:
                await nextTick()

                return {
                    type: 'danger' as UIAlertProps['type'],
                    errorKey: AmountValidationEnum.LOWER_THAN_FEE,
                    message: t('payments.deposit.errors.fee.amountTooLow', { fee: asMoney(Number(fee.value), { decimals: 0 }) })
                }

            default:
                return
        }
    }

    const epayFirstDepositOptionsMutation = useMutation(async (body: Record<string, any> = {}) => {
        if (!selectedPaymentMethod.value) return

        const { data } = await client.POST('/public/cashier/deposit/first-deposit', {
            body: {
                isPlaygame: !!route.meta.isPlaygame,
                brand: site.brand as PaymentServiceComponents['schemas']['Brand'],
                paymentMethod: selectedPaymentMethod.value as unknown as PaymentServiceComponents['schemas']['PaymentMethod'],
                psp: (selectedPaymentMethodObject.value as PaymentMethod | UserAccount)?.psp as PaymentServiceComponents['schemas']['PSP'],
                userAgent: userAgent,
                gameSlug: isGameRestartRequired.value
                    ? (game.value?.combinedGame.slug ?? null)
                    : null,
                gameInstanceId: isGameRestartRequired.value
                    ? game.value?.launch.magnetGameInstanceId || game.value?.launch.gameSessionId
                    : null,
                ...body,
            }
        })

        return data
    })

    async function createRequestObject(): Promise<CashierDepositRequestData | QuickDepositRequestData> {
        if (!userData.value?.sessionId) {
            throw new Error(t('core.defaultErrorMessage'))
        }

        if (!selectedPaymentMethodObject.value || !selectedPaymentMethod.value) {
            throw new Error(t('core.defaultErrorMessage'))
        }

        const baseDataObject: {
            amount: string
            brand: BrandUnion
            fraudnetRiskSessionCorrelationId?: string
            gameSlug?: string
            gameInstanceId?: string
        } = {
            amount: amountValue.value.toString(),
            brand: site.brand,
        }

        if (isGameRestartRequired.value && game.value) {
            baseDataObject.gameSlug = game.value.combinedGame.slug
            baseDataObject.gameInstanceId = game.value.launch.magnetGameInstanceId ?? undefined
        }

        if (isPayPal(selectedPaymentMethod.value)) {
            baseDataObject.fraudnetRiskSessionCorrelationId = 'festfestfestfestdenniserenhest'
        }

        if (!isQuickPay.value) {
            const saveAccount = ((isCreditCard(selectedPaymentMethod.value) && deposit.useStoreAccountCheckbox) || isPayPal(selectedPaymentMethod.value))
                ? storeAccount.value
                : true

            // TypeScript knows this is a CashierDepositDataObject
            const dataObject: Omit<CashierDepositRequestData, 'merchantId' | 'sessionId' | 'url'> & {
                attributes: Omit<CashierDepositDataObjectAttributes, keyof RedirectUrls>
            } = {
                ...baseDataObject,
                userId: userData.value.customerNumber,
                storeAccount: saveAccount,
                attributes: {
                    isQuickPay: false,
                    brand: site.brand,
                    storeAccount: saveAccount,
                }
            }

            if (isCreditCard(selectedPaymentMethod.value)) {
                return { ...dataObject, ...creditCardFields.value }
            }

            if (isMobilepay(selectedPaymentMethod.value)) {
                dataObject.phoneNumber = `+45${userData.value.id.toString().padStart(10, '0')}`
            }

            if (isPayPal(selectedPaymentMethod.value)) {
                dataObject.attributes.useReferenceTransaction = storeAccount.value
            }

            return dataObject
        }

        if (!(selectedPaymentMethodObject.value as UserAccount).id) {
            throw new Error(t('core.defaultErrorMessage'))
        }

        const dataObject: QuickDepositRequestData = {
            ...baseDataObject,
            accountId: (selectedPaymentMethodObject.value as UserAccount).id as string,
            isPlaygame: route.meta.isPlaygame ? 1 : 0,
        }

        return dataObject
    }

    function useUserAccounts(setSelectedAccount: boolean, options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/cashier/deposit/accounts', {
                signal,
                retry: 3,
            })

            if (!data?.accounts) return data

            if (setSelectedAccount) {
                const firstDepositable = data.accounts.find(account => !isDepositPromotionDisabled(account.type) && account.paymentMethodEnabled)

                if (firstDepositable?.id) {
                    selectedAccount.value = firstDepositable.id
                }
            }

            return data.accounts
        }, {
            lazy: true,
            server: false,
            ...options,
            key: 'deposit:user-accounts',
        })
    }

    const getFeeMutation = useMutation(async () => {
        if (!selectedPaymentMethod.value) return
        if (!isFeeEnabledForPaymentMethod(selectedPaymentMethod.value)) return

        if (isInvalidAmount.value) {
            fee.value = null

            return
        }

        const { data } = await client.GET('/public/cashier/deposit/calculate-fee', {
            params: {
                query: {
                    amount: String(toValue(amountValue)),
                    paymentMethod: toValue(selectedPaymentMethod) as unknown as PaymentServiceComponents['schemas']['PaymentMethod'],
                    isBonusToggled: String(toValue(isPromotionToggled) && !isDepositPromotionDisabled()),
                }
            },
            retry: 3,
        })

        return data
    }, {
        onSuccess: (data) => {
            fee.value = data?.fee ? Number(data?.fee) : null
        },
        onError: () => {
            fee.value = null
        }
    })

    const { start: startFeeCalculation, stop: stopFeeCalculation } = useTimeoutFn(async () => {
        await getFeeMutation.mutate({})
    }, 300, { immediate: false })

    const calculateFee = () => {
        fee.value = null

        stopFeeCalculation()
        startFeeCalculation()
    }

    const showUnprocessedWithdrawalsModal = computed(() => {
        if (!unprocessedWithdrawals.value) return

        // Skip the modal when arriving at deposit to claim a bonus - cancelling the withdrawal doesn't make sense here
        if (route.query.claimingBonus) return

        const unprocessedWithdrawalAmountThreshold = 50 // Refers to "50 kr." in withdrawals
        const balanceThreshold = 5 // Refers to "5 kr." on a users' balance

        // Only check balance threshold in a game
        if (route.meta.isPlaygame) {
            if (route.query.view !== DepositViewEnum.CREDIT_AND_PAYMENT) return

            if (game.value) {
                const isBalanceLowerThanThreshold = Number(balance.value) <= balanceThreshold

                if (!isBalanceLowerThanThreshold) return

                // Show the modal if the withdrawal amount is larger than or equal to the unprocessed withdrawal amount threshold
                const isWithdrawalAmountHigherThanThreshold = unprocessedWithdrawals.value?.amount >= unprocessedWithdrawalAmountThreshold

                if (!isWithdrawalAmountHigherThanThreshold) return
            }
        }

        return unprocessedWithdrawals.value?.count > 0
    })

    const depositOptionsMutation = useMutation(async (body: Record<string, any> = {}) => {
        if (!selectedPaymentMethod.value) return

        const { data } = await client.POST('/public/cashier/deposit/deposit-options', {
            body: {
                brand: site.brand as PaymentServiceComponents['schemas']['Brand'],
                isPlaygame: !!route.meta.isPlaygame,
                paymentMethod: selectedPaymentMethod.value as unknown as PaymentServiceComponents['schemas']['PaymentMethod'],
                psp: (selectedPaymentMethodObject.value as PaymentMethod | UserAccount).psp as PaymentServiceComponents['schemas']['PSP'],
                gameSlug: isGameRestartRequired.value
                    ? game.value?.combinedGame?.slug
                    : null,
                gameInstanceId: isGameRestartRequired.value
                    ? game.value?.launch.magnetGameInstanceId || game.value?.launch.gameSessionId
                    : null,
                ...body,
            }
        })

        return data
    })

    async function handleDeposit() {
        trackEvent({
            event_name: 'deposit_handle_deposit_entry',
            event_type: 'Click',
            meta_data: [{
                category: 'Deposit',
                isQuickPay: isQuickPay.value,
                psp: selectedPaymentMethodObject.value?.psp,
                hasSelectedAccount: !!selectedAccount.value,
                selectedAccount: selectedAccount.value,
                userAccountsCount: userAccounts.value?.length ?? 0,
                needsConfirmation: needsConfirmation.value,
                skipNeedsConfirmation: skipNeedsConfirmation.value,
                processing: processing.value,
                hasRequestDataObject: !!requestDataObject.value,
            }]
        })

        if (!selectedPaymentMethodObject.value) {
            trackEvent({
                event_name: 'deposit_handle_deposit_no_selected_method',
                event_type: 'Load',
                meta_data: [{
                    category: 'Deposit',
                    isQuickPay: isQuickPay.value,
                    hasSelectedAccount: !!selectedAccount.value,
                    userAccountsCount: userAccounts.value?.length ?? 0,
                    selectedDepositMethod: selectedDepositMethod.value,
                }]
            })

            return
        }

        if (isFeeLoading.value) {
            return
        }

        // Prevent double clicking but allow forcing process if the popup was blocked
        if (processing.value) {
            trackEvent({
                event_name: 'deposit_handle_deposit_blocked_by_processing',
                event_type: 'Load',
                meta_data: [{ category: 'Deposit', isQuickPay: isQuickPay.value }]
            })

            return
        }

        processing.value = true
        const attempt = startDepositAttempt()

        const amountValidationError = await validateAmount()

        if (amountValidationError) {
            if (isFirstDeposit.value) {
                trackEvent({
                    event_name: 'signup_deposit_validation_error_amount_error',
                    event_type: 'Click',
                    meta_data: [{ category: 'Deposit', errorMessage: amountValidationError.message }]
                })
            }

            resetPayment(amountValidationError.message)

            return
        }

        const consentError = validateConsent()

        if (consentError) {
            if (isFirstDeposit.value) {
                trackEvent({
                    event_name: 'signup_deposit_validation_error_consent_not_given',
                    event_type: 'Click',
                    meta_data: [{ category: 'Deposit', errorMessage: consentError }]
                })
            }

            resetPayment(consentError)

            return
        }

        // A click rejected above starts nothing; from here on it does.
        markDepositStarted()

        if (!hasSavedFirstDepositConsent.value) {
            const body: ConsentComponents['schemas']['ConsentRequest'] = {
                source: `deposit_${route.meta.isPlaygame ? 'playgame' : 'site'}`,
                type: 5,
                version: String(CUSTOMER_FUNDS_CONSENT_VERSION),
                revoked: false,
                verification_status: ConsentRequestVerification_status.ACCEPTED,
                text: depositConsentText.value
            }

            await updateConsent.mutateAsync(body)
        }

        if (!needsConfirmation.value || skipNeedsConfirmation.value) {
            const ePayAmount = getMinorUnitDepositAmount(amountValue.value)

            if (!isQuickPay.value && isPspEpay((selectedPaymentMethodObject.value as PaymentMethod)?.psp)) {
                if (!await ensureEpayLoaded(isExternalPaymentButton.value)) return

                // A card session is set up before the amount is known; from here its failures belong to this submission
                epayAttempt.value = depositAttempt.value

                const ePayOptions = {
                    amount: ePayAmount,
                    store: deposit.useStoreAccountCheckbox && isCreditCard(selectedPaymentMethod.value)
                        ? storeAccount.value
                        : true,
                }

                window.epay.setPaymentOptions(ePayOptions)
            }

            if (
                isPspPaymentIQ((selectedPaymentMethodObject.value as UserAccount | PaymentMethod)?.psp)
                || isPspDummy(selectedPaymentMethodObject.value?.psp)
                || isPspTrueLayer(selectedPaymentMethodObject.value?.psp)
                || (isQuickPay.value && isPspEpay(selectedPaymentMethodObject.value?.psp))
            ) {
                let dataObject: CashierDepositRequestData | QuickDepositRequestData

                try {
                    dataObject = await createRequestObject()
                }
                catch (error) {
                    trackEvent({
                        event_name: 'deposit_create_request_object_error',
                        event_type: 'Load',
                        meta_data: [{
                            category: 'Deposit',
                            errorMessage: (error as Error)?.message,
                            psp: selectedPaymentMethodObject.value?.psp,
                            isQuickPay: isQuickPay.value,
                        }]
                    })

                    if (isFirstDeposit.value) {
                        trackEvent({
                            event_name: 'signup_deposit_endpoint_error_request_object',
                            event_type: 'Load',
                            meta_data: [
                                {
                                    category: 'Deposit',
                                    errorMessage: (error as Error)?.message,
                                },
                            ]
                        })
                    }

                    resetPayment((error as Error)?.message || t('core.defaultErrorMessage'))

                    return
                }

                let depositOptions
                try {
                    depositOptions = await depositOptionsMutation.mutateAsync({}, {
                        onError(error) {
                            const errorMessage = (OpenapiError.isError(error) ? error.cause?.message : undefined) || error?.message || t('core.defaultErrorMessage')

                            trackEvent({
                                event_name: 'deposit_options_mutation_error',
                                event_type: 'Load',
                                meta_data: [{
                                    category: 'Deposit',
                                    errorMessage,
                                    psp: selectedPaymentMethodObject.value?.psp,
                                    isQuickPay: isQuickPay.value,
                                }]
                            })

                            if (isFirstDeposit.value) {
                                trackEvent({
                                    event_name: 'signup_deposit_endpoint_error_deposit_options',
                                    event_type: 'Load',
                                    meta_data: [
                                        {
                                            category: 'Deposit',
                                            errorMessage,
                                        },
                                    ]
                                })
                            }

                            reportDepositFailure(attempt, FailedDepositReason.setup_failed, error)

                            resetPayment(errorMessage as string)
                        }
                    })
                }
                catch {
                    // mutateAsync rethrows after onError; onError already called resetPayment
                    return
                }

                if (isPspEpay(selectedPaymentMethodObject.value?.psp)) {
                    dataObject.amount = ePayAmount
                }

                if (!isQuickPay.value && (isPspDummy(selectedPaymentMethodObject.value?.psp) || isPspTrueLayer(selectedPaymentMethodObject.value?.psp))) {
                    (dataObject as CashierDepositDataObject).paymentMethod = (selectedPaymentMethodObject.value as PaymentMethod).paymentMethod as MethodEnum
                    (dataObject as CashierDepositDataObject).psp = (selectedPaymentMethodObject.value as PaymentMethod).psp as PspEnum
                    (dataObject as CashierDepositDataObject).isPlaygame = !!route.meta.isPlaygame
                }

                requestDataObject.value = defu(dataObject, depositOptions) as unknown as QuickDepositDataObject | CashierDepositDataObject
            }

            if (isFirstDeposit.value) {
                trackEvent({
                    event_name: versionedEventName('signup_confirm_deposit', signupVersion.value),
                    event_type: 'Click',
                    meta_data: [
                        { category: 'Signup' }
                    ]
                })
            }

            if (!skipNeedsConfirmation.value) {
                const promptAmountBump = await tryShowAmountBumpModal(true)

                if (promptAmountBump) return

                startConfirmationDelay()

                return
            }
        }

        if (isQuickPay.value && !requestDataObject.value) {
            trackEvent({
                event_name: 'deposit_handle_deposit_quick_pay_no_request_data',
                event_type: 'Load',
                meta_data: [{
                    category: 'Deposit',
                    psp: selectedPaymentMethodObject.value?.psp,
                    needsConfirmation: needsConfirmation.value,
                    skipNeedsConfirmation: skipNeedsConfirmation.value,
                }]
            })
        }

        const promptAmountBump = await tryShowAmountBumpModal(false)

        if (promptAmountBump) return

        if (hasDepositPromotion.value && isPromotionToggled.value && !isDepositPromotionDisabled()) {
            trackEvent({
                event_name: 'deposit_promotion_activation_started',
                event_type: 'Load',
                meta_data: [{ category: 'Deposit' }],
            })

            try {
                await activatePromotionMutation.mutateAsync({})

                trackEvent({
                    event_name: 'deposit_promotion_activated',
                    event_type: 'Load',
                    meta_data: [{ category: 'Deposit' }],
                })
            }
            catch {
                trackEvent({
                    event_name: 'deposit_promotion_activation_failed',
                    event_type: 'Load',
                    meta_data: [{ category: 'Deposit' }],
                })
            }
        }

        startDeposit()
    }

    function startDeposit() {
        trackEvent({
            event_name: 'deposit_start_deposit',
            event_type: 'Load',
            meta_data: [{
                category: 'Deposit',
                isQuickPay: isQuickPay.value,
                hasRequestDataObject: !!requestDataObject.value,
            }]
        })

        if (!isQuickPay.value) {
            handleFirstDeposit()

            return
        }

        handleQuickDeposit()
    }

    async function goToView(view: DepositViewEnum) {
        await navigateTo({ query: { ...route.query, view } })
    }

    async function openRewardTerms() {
        const termsQuery: { terms: string, referenceId?: string } = { terms: 'true' }

        const referenceId = (promotion.value?.signup ?? promotion.value)?.referenceId

        if (referenceId) termsQuery.referenceId = referenceId

        await navigateTo({ query: { ...route.query, ...termsQuery } })
    }

    async function handleDepositRedirect(redirectUrl?: string) {
        markDepositStarted()

        let redirect: string | undefined = redirectUrl

        if (!redirect) {
            try {
                const response = await getDepositToken.mutateAsync({})

                if (!response?.token) {
                    throw new Error('No token received')
                }

                redirect = `/payments/redirect-assistant?token=${response.token}`
            }
            catch {
                trackEvent({
                    event_name: 'deposit_get_token_error',
                    event_type: 'Load',
                    meta_data: [{ category: 'Deposit', isQuickPay: isQuickPay.value }]
                })

                resetPayment(t('core.defaultErrorMessage'))

                return
            }
        }

        if (game.value) {
            await game.value.gameWrapper?.closeGame()
        }

        if ((requestDataObject.value as CashierDepositDataObject | QuickDepositDataObject)?.gameSlug) {
            delete (requestDataObject.value as CashierDepositDataObject | QuickDepositDataObject).gameSlug
        }

        await navigateTo(redirect, { external: !!redirectUrl })
    }

    function handleQuickDeposit() {
        if (!requestDataObject.value) {
            trackEvent({
                event_name: 'deposit_quick_deposit_no_request_data_object',
                event_type: 'Load',
                meta_data: [{
                    category: 'Deposit',
                    psp: selectedPaymentMethodObject.value?.psp,
                    hasSelectedAccount: !!selectedAccount.value,
                    userAccountsCount: userAccounts.value?.length ?? 0,
                    needsConfirmation: needsConfirmation.value,
                }]
            })

            resetPayment(t('core.defaultErrorMessage'))

            return
        }

        (requestDataObject.value as QuickDepositDataObject).url = '/gateway/payments/api/public/cashier/deposit'

        handleDepositRedirect()
    }

    function getPaymentMethodForApiUsage() {
        if (!selectedPaymentMethodObject.value) return

        const method = (selectedPaymentMethodObject.value as PaymentMethod).paymentMethod.toLowerCase()

        switch (method) {
            case MethodEnum.TRUELAYER.toLowerCase():
                return 'bank'

            default:
                return method
        }
    }

    function makePaymentIqPayment() {
        const paymentMethodForApiUsage = getPaymentMethodForApiUsage()

        if (!paymentMethodForApiUsage) {
            resetPayment(t('core.defaultErrorMessage'))

            return
        }

        (requestDataObject.value as CashierDepositDataObject).url = `${paymentIq.api.replace('https://', '')}/${paymentMethodForApiUsage}/deposit/process?locale=${locale.replace('-', '_')}`;
        (requestDataObject.value as CashierDepositDataObject).attributes.userAgent = userAgent

        handleDepositRedirect()
    }

    function makeDummyPayment() {
        (requestDataObject.value as CashierDepositDataObject).url = '/gateway/payments/api/public/cashier/deposit/first-deposit'

        handleDepositRedirect()
    }

    // TrueLayer has no JS client: the backend responds with an SCARedirect to the
    // Hosted Payments Page, which the redirect-assistant opens.
    function makeTrueLayerPayment() {
        (requestDataObject.value as CashierDepositDataObject).url = '/gateway/payments/api/public/cashier/deposit/first-deposit'

        handleDepositRedirect()
    }

    async function makeEpayPayment() {
        requestDataObject.value = 'epay'

        if (isExternalPaymentButton.value) {
            needsConfirmation.value = true
            processing.value = false

            return
        }

        if (!await ensureEpayLoaded()) return

        window.epay.createCardTransaction()
    }

    async function handleFirstDeposit() {
        if (!selectedPaymentMethodObject.value) return

        if (!await ensureFinancialLimitsWithAcFinten()) return

        if (isPspEpay(selectedPaymentMethodObject.value?.psp)) {
            makeEpayPayment()

            return
        }

        if (!requestDataObject.value) return

        if (isPspPaymentIQ(selectedPaymentMethodObject.value?.psp)) {
            makePaymentIqPayment()

            return
        }

        if (isPspDummy(selectedPaymentMethodObject.value?.psp)) {
            makeDummyPayment()

            return
        }

        if (isPspTrueLayer(selectedPaymentMethodObject.value?.psp)) {
            makeTrueLayerPayment()

            return
        }

        // No branch matched: reset instead of leaving the button spinning forever
        // on `processing` for a PSP this flow does not know how to submit.
        trackEvent({
            event_name: 'deposit_first_deposit_unhandled_psp',
            event_type: 'Load',
            meta_data: [{ category: 'Deposit', psp: selectedPaymentMethodObject.value?.psp }]
        })

        resetPayment(t('core.defaultErrorMessage'))
    }

    async function ensureFinancialLimitsWithAcFinten() {
        if (!useAcFinten.value) return true

        showMoneyLimitsDialog.value = true

        await until(limitAnswerSettled).toBe(true)

        await Promise.all([
            refreshLimitData(),
            invalidateQueries({ key: 'deposit:config', exact: true }),
        ])

        if (requiresLimitAnswer.value) {
            resetPayment()

            return false
        }

        if (isDepositLimitEnabled.value && amountValue.value > remainingLimit.value) {
            resetPayment()

            // The programmatic amount correction re-emits the currency input's `input` event,
            // and setAmount() would wipe the warning below via resetPayment(); the
            // manual-correction flag keeps it alive through that emit.
            isManualCorrectionActive.value = true

            setSelectedAmount(remainingLimit.value)

            message.value = {
                type: 'warning',
                message: t('payments.deposit.adjustedToRemainingLimit', { amount: asMoney(remainingLimit.value, { decimals: 0 }) })
            }

            resetManualCorrection()

            return false
        }

        return true
    }

    function reportSetupFailure(attempt: DepositAttempt | undefined, error: unknown): never {
        reportDepositFailure(attempt, FailedDepositReason.setup_failed, error)

        throw error
    }

    function usePaymentRequest(url: MaybeRefOrGetter<string>, requestData: MaybeRefOrGetter<Record<string, any>>, options: UseQueryOverrideOptions = {}) {
        return useQuery(async () => {
            const attempt = depositAttempt.value

            if ('accountId' in toValue(requestData)) {
                const { data } = await client.POST('/public/cashier/deposit', {
                    body: toValue(requestData) as PaymentServiceComponents['schemas']['QuickDepositRequest'],
                }).catch(error => reportSetupFailure(attempt, error))

                return data as unknown as UnifiedDepositResponse
            }

            const isInternalService = toValue(url).startsWith('/gateway/payments')

            if (isInternalService) {
                // Dynamic internal-service URL isn't in the typed payments surface; use untyped api()
                // scoped to the payments gateway so the request keeps its /gateway/payments base.
                const data = await paymentsApi(toValue(url).replace('/gateway/payments', ''), {
                    method: 'POST',
                    body: toValue(requestData),
                }).catch(error => reportSetupFailure(attempt, error))

                return data as unknown as UnifiedDepositResponse
            }

            const response = await api<CashierDepositResponseData>(`https://${toValue(url).replace('https://', '')}`, {
                method: 'POST',
                body: toValue(requestData),
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                credentials: 'omit'
            }).catch(error => reportSetupFailure(attempt, error))

            return response as UnifiedDepositResponse
        }, {
            queryCache: false,
            lazy: true,
            server: false,
            ...options,
            key: 'deposit:payment-request',
        })
    }

    function useRequestCache(token: MaybeRefOrGetter<string | undefined>, options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/cashier/deposit/request-cache/{token}', {
                signal,
                params: { path: { token: toValue(token) as string } },
                retry: 10,
            })

            await navigateTo({ query: {} }, { replace: true })

            return data
        }, {
            queryCache: false,
            lazy: true,
            server: false,
            enabled: () => !!toValue(token),
            ...options,
            key: `deposit:request-cache`,
        })
    }

    const getDepositToken = useMutation(async () => {
        const reqData = requestDataObject.value
        // request-cache only accepts the cached deposit-data object (not the 'epay' sentinel/undefined)
        if (!selectedPaymentMethod.value || !reqData || typeof reqData !== 'object') return

        // The redirect assistant files a failure under them, and may run in a fresh document without the cashier's state
        const body = 'accountId' in reqData
            ? { ...reqData, psp: selectedPaymentMethodObject.value?.psp, paymentMethod: selectedPaymentMethod.value }
            : reqData

        const { data } = await client.POST('/public/cashier/deposit/request-cache', {
            body: body as unknown as PaymentServiceComponents['schemas']['DepositRequestCacheRequest'],
        })

        return data
    })

    // The backend settles a transaction once it has processed the PSP's notification, which can lag the PSP's own
    // acceptance. Resolves with the last status, still pending if it never settled; throws OpenapiError on a failed
    // request and the signal's reason once aborted.
    async function waitForTransactionStatus(providerTransactionId: string, provider: PspUnion, { signal, maxAttempts = TRANSACTION_STATUS_MAX_ATTEMPTS }: { signal?: AbortSignal, maxAttempts?: number } = {}) {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            signal?.throwIfAborted()

            const { data } = await client.GET('/public/cashier/deposit/status', {
                signal,
                retry: 10,
                params: {
                    query: {
                        providerTransactionId,
                        provider: provider as PaymentServiceComponents['schemas']['PSP']
                    }
                }
            })

            const isSettled = isTransactionSuccessful(data?.state) || isTransactionFailed(data?.state) || isTransactionInconsistent(data?.state)

            if (isSettled || attempt === maxAttempts) return data

            await promiseTimeout(TRANSACTION_STATUS_POLL_INTERVAL)
        }
    }

    // A settled deposit changes balances, counts, promotions and limits
    async function refreshDepositData() {
        await Promise.all([
            invalidateQueries({ key: 'deposit:user-accounts', exact: true }),
            invalidateQueries({ key: 'deposit:payment-methods', exact: true }),
            invalidateQueries({ key: 'deposit:config', exact: true }),
            invalidateQueries({ key: 'deposit:unprocessed-withdrawals', exact: true }),
            invalidateQueries({ key: 'payments:cashier:deposit:successful-count', exact: true }),
            invalidateQueries({ key: 'seamlessWallet:account_details', exact: true }),
            invalidateQueries({ key: 'depositPromotion:pending-offers-with-payment-requirements', exact: true }),
            invalidateQueries({ key: 'affordabilityLimits:get-affordability-limits', exact: true }),
            invalidateQueries({ key: 'moneyLimits:configs', exact: true }),
        ])

        await Promise.all([
            invalidateQueries({ key: 'moneyLimits:slider-options', exact: true }),
            invalidateQueries({ key: 'moneyLimits:limits', exact: true }),
        ])
    }

    function useReceipt(providerTransactionId: MaybeRefOrGetter<string>, provider: MaybeRefOrGetter<PspUnion>, options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/cashier/deposit/receipt', {
                signal,
                params: {
                    query: {
                        providerTransactionId: toValue(providerTransactionId),
                        provider: toValue(provider) as PaymentServiceComponents['schemas']['PSP']
                    }
                }
            })

            if (data?.firstDepositType) {
                const firstDepositingType: FirstDepositTypeUnion = FIRST_DEPOSIT_TYPE_EVENT?.[data.firstDepositType as unknown as FirstDepositTypeResponseEnum] ?? FirstDepositTypeEnum.ULTIMATE

                gtag('event', firstDepositingType)

                zaraz.track(firstDepositingType)

                trackEvent({
                    event_name: `receipt_deposit_${firstDepositingType}`,
                    event_type: 'Click',
                    meta_data: [
                        { category: 'Deposit' }
                    ]
                })
            }

            if (data?.gtmEvent) {
                const { necessary, preferences, statistics, marketing } = cookieConsent.value
                const hasRequiredCookieConsents = necessary && preferences && statistics && marketing

                if (hasRequiredCookieConsents) {
                    await refreshConsent()

                    const emailConsentTypes = deposit.allowedConsentTypesForPurchaseEvent

                    const hasAnyEmailConsent = consents.value?.some(consent =>
                        consent.type
                        && consent.verification_status === ConsentRequestVerification_status.ACCEPTED
                        && emailConsentTypes.includes(consent.type)
                    )

                    if (!hasAnyEmailConsent) {
                        data.gtmEvent.user_data.email = null
                    }
                }
                else {
                    data.gtmEvent.user_data.email = null
                }

                gtag('event', 'purchase', data.gtmEvent)
            }

            trackEvent({
                event_name: 'receipt_deposit',
                event_type: 'Click',
                meta_data: [
                    { category: 'Deposit' }
                ]
            })

            // Outside playgame the receipt shows without waiting for the refetches, as it did before they ran there
            if (route.meta.isPlaygame) {
                await refreshDepositData()
            }
            else {
                void refreshDepositData()

                await navigateTo({ query: omit(router.currentRoute.value.query, RECEIPT_QUERY_KEYS) }, { replace: true })
            }

            return data
        }, {
            lazy: true,
            server: false,
            ...options,
            key: `deposit:receipt-${toValue(provider)}-${toValue(providerTransactionId)}`,
        })
    }

    return {
        bumpAmount,
        FIRST_DEPOSIT_CONSENT_TEXT,
        depositConsentText,
        game,
        isFirstDeposit,
        config,
        paymentMethods,
        loadWalletScripts,
        paymentMethodsData,
        signupVersion,
        fee,
        isQuickPay,
        isSeamlessWallet,
        needsConfirmation,
        selectedAccount,
        selectedDepositMethod,
        showAmountBumpModal,
        creditCardFields,
        isEpayLoaded,
        requestDataObject,
        userAccounts,
        hasAccount,
        hasRejectedSignupPromotion,
        hasShownAmountBumpModal,
        consent,
        signalResetDeposit,
        hasSavedFirstDepositConsent,
        hasFirstDepositConsent,
        hasReachedDepositLimit,
        storeAccount,
        isManualCorrectionActive,
        isFeeEnabled,
        minAmount,
        options,
        isSecondaryView,
        isInvalidAmount,
        canMakeApplePayDeposit,
        canMakeGooglePayDeposit,
        canMakeMobilepayDeposit,
        canMakeExternalPaymentDeposit,
        isExternalPaymentButton,
        selectedPaymentMethodObject,
        selectedPaymentMethod,
        showTransactionOverview,
        creditCardPsp,
        insertEpaySDK,
        ensureEpayLoaded,
        handleEpayTransactionAccepted,
        handleEpayAcceptedRedirect,
        isDepositPromotionDisabled,
        isFeeEnabledForPaymentMethod,
        isFeeLoading,
        formattedPaymentMethodShort,
        getSubtitleForPaymentMethod,
        getProviderDescription,
        getImageForPaymentMethod,
        getDepositRequestAmount,
        getMinorUnitDepositAmount,
        resetPayment,
        startDepositAttempt,
        setDepositAttemptAmount,
        reportDepositFailure,
        resetDepositApp,
        validateConsent,
        validateAmount,
        goToView,
        openRewardTerms,
        useAcFinten,
        ensureFinancialLimitsWithAcFinten,
        useConfig,
        usePaymentMethods,
        usePaymentRequest,
        useRequestCache,
        getDepositToken,
        calculateFee,
        unprocessedWithdrawals,
        showUnprocessedWithdrawalsModal,
        useUnprocessedWithdrawals,
        useUserAccounts,
        handleDeposit,
        handleDepositRedirect,
        refreshConsent,
        getDepositOptions: depositOptionsMutation.mutateAsync,
        waitForTransactionStatus,
        useReceipt,
    }
}
